/**
 * Shared rate limiter for API requests.
 * Used by both Last.fm and Spotify API clients.
 *
 * @param {Object} config - Rate limiting configuration
 * @param {number} config.requestsPerSecond - Max requests per second
 * @param {number} config.maxConcurrent - Max concurrent requests
 * @param {number} config.retryAttempts - Number of retry attempts
 * @param {number} config.baseDelay - Base delay for exponential backoff (ms)
 * @param {number} config.maxDelay - Maximum delay between retries (ms)
 * @param {function} [config.shouldRetry] - Custom retry predicate (receives error, returns boolean)
 */
export class RateLimiter {
    constructor(config) {
        this.config = {
            requestsPerSecond: 5,
            maxConcurrent: 3,
            retryAttempts: 3,
            baseDelay: 1000,
            maxDelay: 30000,
            shouldRetry: null,
            ...config
        };
        this.queue = [];
        this.activeRequests = 0;
        this.lastRequestTime = 0;
        this.processing = false;
    }

    async makeRequest(requestFn) {
        return new Promise((resolve, reject) => {
            this.queue.push({ requestFn, resolve, reject, attempts: 0 });
            this.processQueue();
        });
    }

    async processQueue() {
        if (this.processing || this.queue.length === 0) return;
        if (this.activeRequests >= this.config.maxConcurrent) return;

        this.processing = true;

        while (this.queue.length > 0 && this.activeRequests < this.config.maxConcurrent) {
            const { requestFn, resolve, reject, attempts } = this.queue.shift();

            const timeSinceLastRequest = Date.now() - this.lastRequestTime;
            const minInterval = 1000 / this.config.requestsPerSecond;

            if (timeSinceLastRequest < minInterval) {
                const delay = minInterval - timeSinceLastRequest;
                await this.sleep(delay);
            }

            this.activeRequests++;
            this.lastRequestTime = Date.now();

            try {
                const result = await this.executeWithRetry(requestFn, attempts);
                resolve(result);
            } catch (error) {
                reject(error);
            } finally {
                this.activeRequests--;
            }
        }

        this.processing = false;

        if (this.queue.length > 0) {
            setTimeout(() => this.processQueue(), 100);
        }
    }

    async executeWithRetry(requestFn, attempts) {
        try {
            return await requestFn();
        } catch (error) {
            if (attempts < this.config.retryAttempts && this.defaultShouldRetry(error)) {
                const delay = this.calculateBackoffDelay(attempts);
                console.warn(`API request failed, retrying in ${delay}ms. Attempt ${attempts + 1}/${this.config.retryAttempts}`);
                await this.sleep(delay);
                return this.executeWithRetry(requestFn, attempts + 1);
            }
            throw error;
        }
    }

    defaultShouldRetry(error) {
        if (this.config.shouldRetry) {
            return this.config.shouldRetry(error);
        }
        // Default: retry on network errors and server errors
        if (!error.response && !error.status) return true;
        const status = error.response?.status || error.status;
        return status === 429 || status === 502 || status === 503 || status >= 500;
    }

    calculateBackoffDelay(attempts) {
        const baseDelay = this.config.baseDelay * Math.pow(2, attempts);
        const jitter = Math.random() * 0.1 * baseDelay;
        return Math.min(baseDelay + jitter, this.config.maxDelay);
    }

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    getStats() {
        return {
            queueLength: this.queue.length,
            activeRequests: this.activeRequests,
            isProcessing: this.processing
        };
    }
}
